import React from 'react';
import { Star, MapPin, Clock, Navigation, ArrowRight, ArrowLeft, CalendarPlus, CalendarClock, ChevronUp } from 'lucide-react';
import { formatDistance } from '../../utils/geoDistance';
import {
  formatDurationMinutes,
  parseLocalDateTime,
  addMinutes,
  formatTime,
  formatDateMedium,
} from '../../utils/timeUtils';
import type { RoutyPlaceCard, DriveLeg } from '../../business/routy/routyArtifacts';

interface PlaceCardProps {
  place: RoutyPlaceCard;
  durationMinutes: number;
  disabled?: boolean;
  onSchedule: (place: RoutyPlaceCard, durationMinutes: number) => void;
  /** Marks this as the top route-efficient pick (adds a "Best match" header). */
  recommended?: boolean;
  /** Shows a collapse control so the card can be condensed back into a row. */
  collapsible?: boolean;
  onCollapse?: () => void;
}

function legText(leg: DriveLeg): string {
  const miles = leg.miles != null ? formatDistance(leg.miles) : null;
  const mins = leg.minutes != null ? formatDurationMinutes(leg.minutes, { short: true }) : null;
  const metric = [miles, mins].filter(Boolean).join(' \u00b7 ');
  return metric || 'distance unavailable';
}

const PlaceCard: React.FC<PlaceCardProps> = ({
  place,
  durationMinutes,
  disabled,
  onSchedule,
  recommended,
  collapsible,
  onCollapse,
}) => {
  const proposedStart = place.date && place.startTime ? parseLocalDateTime(place.date, place.startTime) : null;
  const proposedEnd = proposedStart ? addMinutes(proposedStart, durationMinutes) : null;

  return (
    <div
      className={`rounded-lg bg-surface border shadow-sm overflow-hidden ${
        recommended ? 'border-blue-300 ring-1 ring-blue-200' : 'border-ink-200'
      }`}
    >
      {(recommended || collapsible) && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-ink-50 border-b border-ink-200">
          <span className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
            {recommended ? 'Best match' : 'Option'}
          </span>
          {collapsible && onCollapse && (
            <button
              onClick={onCollapse}
              aria-label="Collapse"
              className="text-ink-400 hover:text-ink-600 transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {place.photoUrl && (
        <div className="h-28 w-full bg-ink-100">
          <img src={place.photoUrl} alt={place.name} className="h-full w-full object-cover" loading="lazy" />
        </div>
      )}

      <div className="p-3.5 space-y-2.5">
        {/* Title + rating */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900 truncate">{place.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5 text-xs text-ink-500">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{place.address}</span>
            </div>
          </div>
          {place.rating != null && (
            <div className="flex items-center gap-1 flex-shrink-0 text-xs font-medium text-ink-700">
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              <span>{place.rating.toFixed(1)}</span>
              {place.reviewCount != null && (
                <span className="text-ink-400 font-normal">({place.reviewCount.toLocaleString()})</span>
              )}
            </div>
          )}
        </div>

        {/* Open now */}
        {place.openNow != null && (
          <div className="flex items-center gap-1.5 text-xs">
            <Clock className="w-3 h-3 text-ink-400" />
            <span className={place.openNow ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>
              {place.openNow ? 'Open now' : 'Closed now'}
            </span>
          </div>
        )}

        {/* Route strip — the Routemize signal */}
        {(place.driveIn || place.driveOut) && (
          <div className="rounded-lg bg-blue-50/70 border border-blue-100 px-3 py-2 space-y-1.5">
            {place.driveIn && (
              <div className="flex items-start gap-1.5 text-xs text-ink-700">
                <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-blue-500" />
                <span>
                  <span className="font-semibold">{legText(place.driveIn)}</span> from {place.driveIn.label}
                </span>
              </div>
            )}
            {place.driveOut && (
              <div className="flex items-start gap-1.5 text-xs text-ink-700">
                <ArrowLeft className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-indigo-500" />
                <span>
                  <span className="font-semibold">{legText(place.driveOut)}</span> to {place.driveOut.label}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Top review */}
        {place.topReview?.text && (
          <p className="text-xs text-ink-500 italic line-clamp-2">
            &ldquo;{place.topReview.text}&rdquo; &mdash; {place.topReview.author}
          </p>
        )}

        {/* Proposed time — what Routy will book */}
        {proposedStart && proposedEnd && (
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
            <CalendarClock className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-2xs font-medium text-emerald-700 uppercase tracking-wide">Routy proposes</p>
              <p className="text-sm font-semibold text-ink-900">
                {formatDateMedium(proposedStart)} · {formatTime(proposedStart)} – {formatTime(proposedEnd)}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-0.5">
          {place.mapsUrl && (
            <a
              href={place.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-ink-100 text-ink-700 text-xs font-medium hover:bg-ink-200 transition-colors"
            >
              <Navigation className="w-3.5 h-3.5" />
              Directions
            </a>
          )}
          <button
            onClick={() => onSchedule(place, durationMinutes)}
            disabled={disabled}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CalendarPlus className="w-3.5 h-3.5" />
            Schedule {durationMinutes} min
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlaceCard;
